import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../firebase';

// Shared Google + Apple sign-in buttons for /login and /register (single source so both stay in
// sync — mirrors the Flutter login screen). OAuth accounts come back with a verified email, so they
// sail through RequireAuth's email-verification gate with no extra step.
//
// Requires the providers to be enabled in the Firebase console (Authentication → Sign-in method):
//   • Google — works out of the box once enabled.
//   • Apple  — needs an Apple Service ID + key configured on the provider, and sphere-sync.com listed
//     as an authorized domain. Until then the button surfaces "auth/operation-not-allowed".
const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

// Returns a user-facing message, or null when it's just a cancel (don't nag on those).
function describe(err) {
  switch (err?.code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
    case 'auth/user-cancelled':
      return null; // user backed out
    case 'auth/account-exists-with-different-credential':
      return 'You already have an account with this email. Sign in with the method you used originally.';
    case 'auth/operation-not-allowed':
      return 'That sign-in option isn’t enabled yet. Use email and password for now.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup. Allow popups for this site and try again.';
    case 'auth/unauthorized-domain':
      // This site's domain isn't in Firebase Auth → Settings → Authorized domains — the popup
      // opens and immediately closes itself. (Bit us in prod: sphere-sync.com was never added.)
      return 'Sign-in isn’t authorized for this site’s address yet. Please use email and password, and let us know.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      // Surface the code: a generic "failed" hid auth/unauthorized-domain for weeks.
      return `Sign-in failed. Please try again.${err?.code ? ` (${err.code})` : ''}`;
  }
}

export default function SocialAuth({ dest = '/app', onError }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const run = async (provider) => {
    setBusy(true);
    onError?.(null);
    try {
      await signInWithPopup(auth, provider);
      // Signed in + verified now — go where they were headed (Login also has an effect that does
      // this; both target the same dest with replace, so the overlap is harmless).
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = describe(err);
      if (msg) onError?.(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="db-social-group">
      <div className="db-or"><span>or</span></div>
      <button
        type="button" className="db-social db-social-google"
        disabled={busy} onClick={() => run(googleProvider)}
      >
        <span className="db-social-icon" aria-hidden="true">
          <svg viewBox="0 0 18 18" width="18" height="18">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.88 2.68-6.62z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/>
            <path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"/>
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/>
          </svg>
        </span>
        Continue with Google
      </button>
      <button
        type="button" className="db-social db-social-apple"
        disabled={busy} onClick={() => run(appleProvider)}
      >
        <span className="db-social-icon" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor">
            <path d="M11.18 8.46c-.02-1.7 1.39-2.51 1.45-2.55-.79-1.16-2.02-1.32-2.46-1.34-1.05-.11-2.04.61-2.57.61-.53 0-1.35-.6-2.22-.58-1.14.02-2.19.66-2.78 1.68-1.18 2.06-.3 5.1.85 6.77.56.82 1.23 1.73 2.11 1.7.85-.03 1.17-.55 2.19-.55 1.02 0 1.31.55 2.21.53.91-.02 1.49-.83 2.05-1.65.65-.95.92-1.87.93-1.92-.02-.01-1.78-.69-1.8-2.93zM9.5 3.6c.47-.57.79-1.36.7-2.15-.68.03-1.5.45-1.98 1.02-.43.5-.81 1.31-.71 2.08.76.06 1.53-.39 1.99-.95z"/>
          </svg>
        </span>
        Continue with Apple
      </button>
    </div>
  );
}
